import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const analysisQueryKey = (id: string) => ['analysis', id] as const;
export const analysisPagesQueryKey = (id: string) => ['analysisPages', id] as const;

export const fetchAnalysisById = async (id: string) => {
  const { data, error } = await supabase
    .from("analyses")
    .select("*")
    .eq("id", id)
    .maybeSingle();

  if (error) throw error;
  if (!data) throw new Error("not_found");
  return data;
};

const fetchAnalysisPages = async (analysisId: string) => {
  const { data, error } = await supabase
    .from("analysis_pages")
    .select("*")
    .eq("analysis_id", analysisId)
    .order("page_number", { ascending: true });

  if (error) throw error;
  return data || [];
};

const STALE_TIME = 5 * 60 * 1000; // 5 minutes
const GC_TIME = 30 * 60 * 1000; // 30 minutes

export function useAnalysisData(id: string | undefined) {
  const queryClient = useQueryClient();

  const analysisQuery = useQuery({
    queryKey: analysisQueryKey(id!),
    queryFn: () => fetchAnalysisById(id!),
    enabled: !!id,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });

  const pagesQuery = useQuery({
    queryKey: analysisPagesQueryKey(id!),
    queryFn: () => fetchAnalysisPages(id!),
    enabled: !!id && !!analysisQuery.data?.page_count && analysisQuery.data.page_count > 1,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });

  const updateAnalysisCache = (updater: (prev: any) => any) => {
    if (!id) return;
    queryClient.setQueryData(analysisQueryKey(id), (prev: any) => {
      if (!prev) return prev;
      return updater(prev);
    });
  };

  return {
    analysis: analysisQuery.data ?? null,
    pages: pagesQuery.data ?? [],
    isLoading: analysisQuery.isLoading,
    isError: analysisQuery.isError,
    error: analysisQuery.error,
    updateAnalysisCache,
    queryClient,
  };
}

export function usePrefetchAnalysis() {
  const queryClient = useQueryClient();

  return (analysisId: string) => {
    queryClient.prefetchQuery({
      queryKey: analysisQueryKey(analysisId),
      queryFn: () => fetchAnalysisById(analysisId),
      staleTime: STALE_TIME,
    });
  };
}
